import * as i0 from '@angular/core';
import { Injectable, Directive, Input, HostListener } from '@angular/core';
import * as i1 from '@dch/ng-common/src/lib/shared';

class AnalyticsService {
    constructor(windowRef) {
        this.windowRef = windowRef;
        this.data = {
            applicationTags: [],
            adobeData: { page: { category: { l3: '' } } },
            reportingComponentsQuantity: 0,
        };
        this.windowRef.nativeWindow.onbeforeunload = (event) => {
            if (this.data.reportingComponentsQuantity !== 0) {
                this.sendEventsToTMS(this.data.adobeData);
            }
        };
    }
    reportEvent(adobeData) {
        if (adobeData) {
            this.sendEventsToTMS(adobeData);
        }
    }
    reportErrorEvent(message, errorCode, errorType) {
        if (message) {
            const data = {
                event: 'card-click-impression',
                card: {
                    zone: this.data.adobeData.page.name,
                    name: this.data.adobeData.page.category.l3,
                },
                error: {
                    type: errorType,
                    message,
                    detail: errorCode,
                },
            };
            this.sendEventsToTMS(data);
        }
    }
    isElementInViewport(element) {
        const rect = element.getBoundingClientRect();
        const documentElement = this.windowRef.nativeWindow.document.documentElement;
        const isHidden = element.offsetParent == null;
        return (!!rect &&
            !isHidden &&
            rect.bottom >= 0 &&
            rect.right >= 0 &&
            rect.top <= documentElement.clientHeight &&
            rect.left <= documentElement.clientWidth);
    }
    addApplicationTag(name, value) {
        this.data.applicationTags.push({ name, value });
    }
    registerComponent() {
        this.data.reportingComponentsQuantity++;
    }
    deRegisterComponent() {
        this.setReportData({ event: 'page_view' });
    }
    setReportData(data) {
        clearTimeout(this.componentDataTimer);
        this.data.adobeData = this.appendContext(data);
        this.data.reportingComponentsQuantity--;
        this.componentDataTimer = setTimeout(() => {
            this.sendTMSReportIfDataResolved();
        }, 500);
    }
    getAnalyticsEventData(analytics, userLanguage, customerAccount, queryParams) {
        return {
            event: 'page-impression',
            page: {
                ...analytics.page,
                segment: analytics.page && analytics.page.segment
                    ? analytics.page.segment
                    : this.getTaggingSegment(customerAccount.getSegment()),
                site: 'personalised cockpit (pcp)',
                language: userLanguage,
            },
            customer: analytics.customer || this.getCustomer(customerAccount),
            user: analytics.user || this.getUser(customerAccount),
            params: queryParams,
            ...(analytics.context && { context: analytics.context }),
            ...(analytics.message && {
                message: { info: analytics.message?.info },
            }),
        };
    }
    appendContext(analyticsEventData) {
        if (analyticsEventData.context && this.data.adobeData.context) {
            analyticsEventData.context = analyticsEventData.context.concat(';', this.data.adobeData.context);
        }
        return Object.assign({}, this.data.adobeData, analyticsEventData);
    }
    sendTMSReportIfDataResolved() {
        if (this.data.reportingComponentsQuantity === 0) {
            this.sendEventsToTMS(this.data.adobeData);
        }
    }
    sendSingleEventToTMS(eventData) {
        // both keys and values should be provided in lowercase
        pxdatalayer.push(JSON.parse(JSON.stringify(eventData).toLowerCase()));
    }
    sendEventsToTMS(adobeData) {
        const adobeDataClone = Object.assign({}, adobeData);
        if (adobeData.page && adobeData.page.category && adobeData.page.category.l3) {
            this.data.adobeData.page.category.l3 = adobeData.page.category.l3;
            this.data.adobeData.page.name = adobeData.page.name;
        }
        this.data.applicationTags.forEach(tag => {
            adobeDataClone[tag.name] = tag.value;
        });
        this.sendSingleEventToTMS(adobeDataClone);
    }
    getCustomer(customerAccount) {
        const selectedCustomer = customerAccount.getSelectedCustomer();
        if (selectedCustomer) {
            return {
                id: selectedCustomer.customerId,
                segment: this.getTaggingSegment(customerAccount.getSegment()),
                subsegment: selectedCustomer.subsegment,
            };
        }
        else {
            return undefined;
        }
    }
    getUser(customerAccount) {
        const selectedCustomer = customerAccount.getSelectedCustomer();
        if (selectedCustomer) {
            return {
                role: selectedCustomer.payer ? 'au' : 'ur',
                gid: customerAccount.globalId,
                msisdn: customerAccount.getOwnedMsisdns() && customerAccount.getOwnedMsisdns().length > 0
                    ? customerAccount.getOwnedMsisdns()[0].msisdn
                    : selectedCustomer.contactNumber,
            };
        }
        else {
            return undefined;
        }
    }
    getTaggingSegment(segment) {
        switch (segment) {
            case 'RES':
                return 'private';
            case 'PRE':
                return 'private';
            case 'SE':
                return 'self employed and small companies';
            case 'COR':
                return 'large';
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: AnalyticsService, deps: [{ token: i1.WindowRef }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: AnalyticsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.WindowRef }] });

class PxAnalyticsElementClicked {
    constructor(analyticsService) {
        this.analyticsService = analyticsService;
    }
    onClick($event) {
        const adobeData = { ...this.pxAnalyticsElementClicked };
        this.analyticsService.reportEvent(adobeData);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: PxAnalyticsElementClicked, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: PxAnalyticsElementClicked, isStandalone: true, selector: "[pxAnalyticsElementClicked]", inputs: { pxAnalyticsElementClicked: "pxAnalyticsElementClicked" }, host: { listeners: { "click": "onClick($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: PxAnalyticsElementClicked, decorators: [{
            type: Directive,
            args: [{
                    selector: '[pxAnalyticsElementClicked]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: AnalyticsService }], propDecorators: { pxAnalyticsElementClicked: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class PxAnalyticsElementPageView {
    constructor(analyticsService) {
        this.analyticsService = analyticsService;
        const utagData = {
            event: 'page_view',
        };
        this.analyticsService.reportEvent(utagData);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: PxAnalyticsElementPageView, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: PxAnalyticsElementPageView, isStandalone: true, selector: "[pxAnalyticsElementPageView]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: PxAnalyticsElementPageView, decorators: [{
            type: Directive,
            args: [{
                    selector: '[pxAnalyticsElementPageView]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: AnalyticsService }] });

class PxAnalyticsError {
    constructor(analyticsService) {
        this.analyticsService = analyticsService;
    }
    ngOnInit() {
        this.subscription = this.pxAnalyticsError.subscribe(error => {
            this.analyticsService.reportErrorEvent(error.message, error.code);
        });
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: PxAnalyticsError, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.1", type: PxAnalyticsError, isStandalone: true, selector: "[pxAnalyticsError]", inputs: { pxAnalyticsError: "pxAnalyticsError" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: PxAnalyticsError, decorators: [{
            type: Directive,
            args: [{
                    selector: '[pxAnalyticsError]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: AnalyticsService }], propDecorators: { pxAnalyticsError: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsService, PxAnalyticsElementClicked, PxAnalyticsElementPageView, PxAnalyticsError };
